import { useContext } from "react";

import { useTranslation } from "react-i18next";
import { createSearchParams, useNavigate } from "react-router";

import { css } from "@emotion/react";

import { colors } from "@dotnet/telematics-design-system";

import TextLink, {
    TEXT_LINK_SIZES
} from "components/DesignComponents/TextLink";
import {
    TOOLTIP_PLACEMENT,
    TooltipOptional
} from "components/DesignComponents/Tooltips";
import { QUERY_FIELDS } from "components/Search/types";
import ParamsContext from "context/params/paramsContext";
import { useWindowLocation } from "shared/hooks/useWindowLocation";
import { FILTER_NAMES, NOT_FILTER_NAMES, ROUTES } from "shared/types/enums";

import { FoundResourceDetailsProps } from "../types/interfaces";
import FoundResourceCompanyRow from "./FoundResourceCompanyRow";
import FoundResourceInfoRow from "./FoundResourceInfoRow";

const FoundResourceDetails = ({
    resourceData,
    tabData,
    close
}: FoundResourceDetailsProps) => {
    const { gray700, gray900 } = colors;

    const { queryParams, changeRootCompany, resetAllParams, setQueryParams } =
        useContext(ParamsContext);

    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useWindowLocation();

    const {
        imei,
        email,
        company_id,
        company_name,
        company_access_group_name,
        controlled_by_user
    } = resourceData;

    const { label, resourceRoute } = tabData;

    const handleLinkClick = () => {
        const rootCompanyId = queryParams[FILTER_NAMES.RootCompany] as
            string | undefined;

        const queryResource = resourceRoute === ROUTES.Devices ? imei : email;

        const queryField =
            resourceRoute === ROUTES.Devices
                ? QUERY_FIELDS.Imei
                : QUERY_FIELDS.Email;

        const updatedParams = {
            [NOT_FILTER_NAMES.Query]: String(queryResource),
            [NOT_FILTER_NAMES.QueryField]: queryField,
            [FILTER_NAMES.RootCompany]: String(company_id)
        };

        const isSameCompany = String(company_id) === rootCompanyId;
        const isSameRoute = pathname === resourceRoute;

        resetAllParams();

        if (isSameCompany) {
            isSameRoute
                ? setQueryParams({
                      params: updatedParams,
                      resetParams: true
                  })
                : navigate({
                      pathname: resourceRoute,
                      search: createSearchParams(updatedParams).toString()
                  });
        } else {
            !isSameRoute &&
                navigate({
                    pathname: resourceRoute,
                    search: createSearchParams(updatedParams).toString()
                });

            changeRootCompany(String(company_id), updatedParams);
        }

        close();
    };

    return resourceData ? (
        <div
            css={css({
                marginTop: "14px",

                "> div": {
                    display: "flex",
                    gridTemplateColumns: "auto auto",
                    padding: "4px 0"
                },

                "> div:last-of-type": {
                    padding: "4px 0 0"
                },

                "div > span": {
                    fontSize: "14px",
                    fontWeight: "500",
                    lineHeight: "20px",
                    letterSpacing: "0.1px"
                },

                "div > span:first-of-type": {
                    color: gray700,
                    minWidth: "136px",
                    paddingRight: "4px"
                },

                "div > span:last-of-type": {
                    color: gray900,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                }
            })}
        >
            <FoundResourceInfoRow
                resourceData={resourceData}
                resourceRoute={resourceRoute}
            />

            <FoundResourceCompanyRow
                companyType={t("Dialog##company")}
                companyName={company_name}
            />

            <FoundResourceCompanyRow
                companyType={t("Table##access group")}
                companyName={company_access_group_name}
            />

            <div>
                <TooltipOptional
                    title={t(`Dialog##find tool##${label}##no permission`)}
                    placement={TOOLTIP_PLACEMENT.Top}
                    isHidden={controlled_by_user}
                >
                    <div>
                        <TextLink
                            onClick={handleLinkClick}
                            size={TEXT_LINK_SIZES.Normal}
                            isDisabled={!controlled_by_user}
                        >
                            {t(`Dialog##find tool##${label}##link`)}
                        </TextLink>
                    </div>
                </TooltipOptional>
            </div>
        </div>
    ) : (
        <></>
    );
};

export default FoundResourceDetails;
